<template>
  <div class="rider-tracking-wrap">
    <!-- Google Map (แสดงตลอด ไม่ซ่อน) -->
    <div ref="mapEl" class="tracking-map-el" :style="{ height }"></div>

    <!-- Overlay รอ location — แสดงแค่ช่วงแรก -->
    <div v-if="waiting && !timedOut" class="tracking-overlay">
      <v-progress-circular indeterminate color="primary" size="24" class="mr-2" />
      <span class="caption grey--text">กำลังรับตำแหน่งไรเดอร์...</span>
    </div>
    <div v-if="timedOut && waiting" class="tracking-overlay tracking-overlay--warn">
      <v-icon small color="warning" class="mr-1">mdi-map-marker-off</v-icon>
      <span class="caption">ไรเดอร์ยังไม่ได้อัปเดตตำแหน่ง</span>
    </div>

    <!-- ETA bar -->
    <div v-if="etaText" class="eta-bar px-3 py-2 d-flex align-center">
      <v-icon small color="primary" class="mr-1">mdi-clock-fast</v-icon>
      <span class="caption font-weight-medium">ถึงใน {{ etaText }}</span>
      <span v-if="distText" class="caption grey--text ml-2">· {{ distText }}</span>
      <v-spacer />
      <span class="caption grey--text">อัปเดตอัตโนมัติ</span>
    </div>
  </div>
</template>

<script>
/* global google */
import { loadGoogleMapsScript } from "@/utils/googleMapsLoader";

const REROUTE_DISTANCE_M = 100;   // เกิน 100 เมตร → เส้นทางใหม่
const REROUTE_COOLDOWN_MS = 60000; // re-route ได้ทุก 60 วิ

function haversineMeters(a, b) {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export default {
  name: "RiderTrackingMap",
  props: {
    riderId: { type: String, required: true },
    destination: { type: Object, required: true }, // { lat, lng }
    destinationLabel: { type: String, default: "จุดหมาย" },
    origin: { type: Object, default: null }, // { lat, lng } จุดรับ
    originLabel: { type: String, default: "จุดรับ" },
    height: { type: String, default: "280px" },
  },
  data() {
    return {
      map: null,
      riderMarker: null,
      destMarker: null,
      originMarker: null,
      directionsRenderer: null,
      directionsService: null,
      riderLocation: null,
      waiting: true,
      timedOut: false,
      waitTimer: null,
      etaText: null,
      distText: null,
      lastRoutedPos: null,
      lastRoutedAt: 0,
    };
  },
  async mounted() {
    if (this.$socket && this.riderId) {
      this.$socket.emit("user:trackRider", this.riderId);
      this._locationHandler = (data) => {
        this.onRiderLocation(data);
      };
      this.$socket.on(`rider:location:${this.riderId}`, this._locationHandler);
    }
    await loadGoogleMapsScript();
    this.initMap();
    // ถ้า 15 วิยังไม่มี location → แสดง warn แทน spinner
    this.waitTimer = setTimeout(() => { this.timedOut = true; }, 15000);
  },
  beforeDestroy() {
    if (this.$socket && this._locationHandler) {
      this.$socket.off(`rider:location:${this.riderId}`, this._locationHandler);
    }
    clearTimeout(this.waitTimer);
  },
  methods: {
    initMap() {
      const center = { lat: this.destination.lat, lng: this.destination.lng };
      this.map = new google.maps.Map(this.$refs.mapEl, {
        center,
        zoom: 14,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: "cooperative",
      });

      // Pin SVG with drop-shadow for destination
      const destSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
          <defs>
            <filter id="shadow" x="-30%" y="-20%" width="160%" height="160%">
              <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#00000055"/>
            </filter>
          </defs>
          <path filter="url(#shadow)"
            d="M18 2C10.268 2 4 8.268 4 16c0 10.5 14 30 14 30S32 26.5 32 16C32 8.268 25.732 2 18 2z"
            fill="#DC2626"/>
          <circle cx="18" cy="16" r="7" fill="#fff" opacity="0.95"/>
          <circle cx="18" cy="16" r="4" fill="#DC2626"/>
        </svg>`;
      this.destMarker = new google.maps.Marker({
        map: this.map,
        position: center,
        title: this.destinationLabel,
        icon: {
          url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(destSvg),
          scaledSize: new google.maps.Size(36, 48),
          anchor: new google.maps.Point(18, 48),
        },
      });

      new google.maps.InfoWindow({ content: `<b>${this.destinationLabel}</b>` }).open(
        this.map,
        this.destMarker
      );

      // Origin marker (จุดรับ — เขียว)
      if (this.origin) {
        const originSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
            <defs>
              <filter id="oshadow" x="-30%" y="-20%" width="160%" height="160%">
                <feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-color="#00000055"/>
              </filter>
            </defs>
            <path filter="url(#oshadow)"
              d="M18 2C10.268 2 4 8.268 4 16c0 10.5 14 30 14 30S32 26.5 32 16C32 8.268 25.732 2 18 2z"
              fill="#16A34A"/>
            <circle cx="18" cy="16" r="7" fill="#fff" opacity="0.95"/>
            <circle cx="18" cy="16" r="4" fill="#16A34A"/>
          </svg>`;
        this.originMarker = new google.maps.Marker({
          map: this.map,
          position: this.origin,
          title: this.originLabel,
          icon: {
            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(originSvg),
            scaledSize: new google.maps.Size(36, 48),
            anchor: new google.maps.Point(18, 48),
          },
        });
        new google.maps.InfoWindow({ content: `<b>${this.originLabel}</b>` }).open(
          this.map,
          this.originMarker
        );

        // fit bounds ให้เห็นทั้งสองหมุด
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(this.origin);
        bounds.extend(center);
        this.map.fitBounds(bounds, { top: 60, right: 40, bottom: 60, left: 40 });
      }

      this.directionsService = new google.maps.DirectionsService();
      this.directionsRenderer = new google.maps.DirectionsRenderer({
        map: this.map,
        suppressMarkers: true,
        polylineOptions: { strokeColor: "#2563EB", strokeWeight: 5, strokeOpacity: 0.85 },
      });
    },

    onRiderLocation(data) {
      const loc = { lat: Number(data.latitude), lng: Number(data.longitude) };
      this.riderLocation = loc;
      this.waiting = false;
      this.timedOut = false;
      clearTimeout(this.waitTimer);

      if (!this.map) return;

      if (!this.riderMarker) {
        const riderSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
            <defs>
              <filter id="rshadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#00000055"/>
              </filter>
            </defs>
            <text filter="url(#rshadow)" x="24" y="38" text-anchor="middle" font-size="38" fill="#000">🛵</text>
          </svg>`;
        this.riderMarker = new google.maps.Marker({
          map: this.map,
          position: loc,
          title: "ไรเดอร์",
          icon: {
            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(riderSvg),
            scaledSize: new google.maps.Size(56, 56),
            anchor: new google.maps.Point(28, 28),
          },
          animation: google.maps.Animation.DROP,
          zIndex: 100,
        });
      } else {
        this.riderMarker.setPosition(loc);
      }

      // ตรวจว่าต้อง re-route ไหม
      const moved = this.lastRoutedPos
        ? haversineMeters(this.lastRoutedPos, loc)
        : Infinity;
      const elapsed = Date.now() - this.lastRoutedAt;

      if (moved > REROUTE_DISTANCE_M && elapsed > REROUTE_COOLDOWN_MS) {
        this.fetchRoute(loc);
      }
    },

    fetchRoute(origin) {
      const from = origin || this.riderLocation;
      if (!from || !this.destination || !this.directionsService) return;

      this.lastRoutedPos = { ...from };
      this.lastRoutedAt = Date.now();

      this.directionsService.route(
        {
          origin: new google.maps.LatLng(from.lat, from.lng),
          destination: new google.maps.LatLng(this.destination.lat, this.destination.lng),
          travelMode: google.maps.TravelMode.DRIVING,
          region: "TH",
        },
        (result, status) => {
          if (status !== "OK") return;
          this.directionsRenderer.setDirections(result);
          const leg = result.routes[0].legs[0];
          this.etaText = leg.duration.text;
          this.distText = leg.distance.text;

          const bounds = new google.maps.LatLngBounds();
          bounds.extend(new google.maps.LatLng(from.lat, from.lng));
          bounds.extend(new google.maps.LatLng(this.destination.lat, this.destination.lng));
          this.map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
        }
      );
    },
  },
};
</script>

<style scoped>
.rider-tracking-wrap {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}
.tracking-map-el { width: 100%; }
.tracking-overlay {
  position: absolute;
  top: 10px; left: 50%; transform: translateX(-50%);
  background: rgba(255,255,255,0.92);
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 6px 14px;
  display: flex; align-items: center;
  z-index: 5;
  pointer-events: none;
  white-space: nowrap;
}
.tracking-overlay--warn { border-color: #fde68a; background: #fffbeb; }
.eta-bar {
  background: #fff;
  border-top: 1px solid #e2e8f0;
}
</style>
